<template>
  <div>
    <!-- ============================================================== -->
    <!-- Bread crumb -->
    <!-- ============================================================== -->
    <div class="page-breadcrumb border-bottom">
      <div class="row">
        <div class="col-lg-3 col-md-4 col-xs-12 align-self-center">
          <h5 class="mb-0 font-medium text-uppercase">
            Onboarding Journey <span class="badge badge-primary">{{ flowConfigs.data.length }}</span>
          </h5>
        </div>
        <div class="col-lg-9 col-md-8 col-xs-12 align-self-center">
          <nav aria-label="breadcrumb" class="float-left mt-2 float-md-right">
            <ol class="p-0 mb-0 breadcrumb justify-content-end">
              <li class="breadcrumb-item">
                <a>Home</a>
              </li>
              <li class="breadcrumb-item active" aria-current="page">Onboarding Journey</li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
    <!-- ============================================================== -->
    <!-- Container fluid -->
    <!-- ============================================================== -->
    <div class="page-content container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="d-flex justify-content-end align-items-center mb-3">
            <b-button size="sm" variant="outline-secondary" class="mr-2" @click="openGlossary">
              <i class="fa fa-book"></i> Glossary
            </b-button>
            <b-button size="sm" variant="primary" @click="openCreateJourney">
              <i class="fa fa-plus"></i> New journey
            </b-button>
          </div>
          <b-tabs v-model="activeArea" content-class="mt-3">
            <!-- ===================== AREA 1: HEALTH & ACTIONS ===================== -->
            <b-tab title="Health &amp; Actions" active>
              <div class="card">
                <div class="card-body">
                  <div class="text-center">
                    <i v-if="health.loading" class="fa fa-spinner fa-spin"></i>
                  </div>

                  <div v-if="health.error" class="alert alert-warning">{{ health.error }}</div>

                  <template v-else-if="!health.loading">
                    <div class="row">
                      <div class="col-md-4 col-12 mb-3">
                        <div class="text-uppercase text-muted" style="font-size:11px">Journeys · last 24h</div>
                        <div class="font-weight-bold" style="font-size:28px">{{ (health.data.journeys || {}).last24h || 0 }}</div>
                        <div class="text-muted" style="font-size:11px">{{ (health.data.journeys || {}).total || 0 }} all time</div>
                      </div>
                      <div class="col-md-4 col-12 mb-3">
                        <div class="text-uppercase text-muted" style="font-size:11px">Fallback share</div>
                        <div
                          class="font-weight-bold"
                          style="font-size:28px"
                          :class="{ 'text-warning': fallbackShareRatio > 0.05 }"
                        >{{ fallbackSharePercent }}</div>
                        <div class="text-muted" style="font-size:11px">healthy at or below 5%</div>
                      </div>
                      <div class="col-md-4 col-12 mb-3">
                        <div class="text-uppercase text-muted" style="font-size:11px">Open issues</div>
                        <div
                          class="font-weight-bold"
                          style="font-size:28px"
                          :class="{ 'text-danger': configFaultCount > 0 }"
                        >{{ configFaultCount }}</div>
                        <div class="text-muted" style="font-size:11px">items in the attention queue</div>
                      </div>
                    </div>

                    <h6 class="text-uppercase text-muted mt-2" style="font-size:11px">Needs attention</h6>
                    <div v-if="!needsAttentionQueue.length" class="alert alert-success mb-3">
                      <i class="fa fa-check-circle"></i>
                      Nothing needs attention: no orphan arms, every enabled config is well formed, and fallback share is within threshold.
                    </div>
                    <div v-else class="list-group mb-3">
                      <div
                        v-for="row in needsAttentionQueue"
                        :key="row.key"
                        class="list-group-item d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <span
                            class="badge mr-2"
                            :class="row.severity === 'critical' ? 'badge-danger' : 'badge-warning'"
                          >{{ row.severity === 'critical' ? 'Critical' : 'Warning' }}</span>
                          {{ row.description }}
                        </div>
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          class="ml-2 text-nowrap"
                          @click="resolveAttention(row)"
                        >{{ row.actionLabel }}</b-button>
                      </div>
                    </div>

                    <template v-if="resolutionSourceRows.length">
                      <h6 class="text-uppercase text-muted" style="font-size:11px">Resolution mix</h6>
                      <div class="progress mb-2" style="height:12px">
                        <div
                          v-for="(row, index) in resolutionSourceRows"
                          :key="row.key"
                          class="progress-bar"
                          :style="{ width: row.percent + '%', backgroundColor: armColour(index) }"
                          :title="row.key + ': ' + row.percent + '%'"
                        ></div>
                      </div>
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <thead>
                            <tr>
                              <th>Source</th>
                              <th>Share</th>
                              <th>Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(row, index) in resolutionSourceRows" :key="row.key">
                              <td>
                                <span
                                  class="d-inline-block rounded-circle mr-1 align-middle"
                                  :style="{ width: '10px', height: '10px', backgroundColor: armColour(index) }"
                                ></span>
                                {{ row.key }}
                              </td>
                              <td>{{ row.percent }}%</td>
                              <td class="text-muted">{{ row.value }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </template>

                    <div class="alert alert-info mt-3" style="font-size:12px">
                      <i class="fa fa-bar-chart"></i>
                      {{ runningExperimentCount }} live test{{ runningExperimentCount === 1 ? '' : 's' }} running.
                      The configured arm split for each live test is on the Targeting tab.
                    </div>
                  </template>
                </div>
              </div>
            </b-tab>

            <!-- ===================== AREA 2: TARGETING (CRUD) ===================== -->
            <b-tab title="Targeting">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h6 class="text-uppercase text-muted mb-0" style="font-size:11px">Targeting configs</h6>
                    <b-button size="sm" variant="primary" @click="openCreateJourney">
                      <i class="fa fa-plus"></i> New journey
                    </b-button>
                  </div>

                  <div class="text-center">
                    <i v-if="flowConfigs.loading" class="fa fa-spinner fa-spin"></i>
                  </div>

                  <div v-if="flowConfigs.error" class="alert alert-warning">{{ flowConfigs.error }}</div>

                  <template v-else-if="!flowConfigs.loading">
                    <data-list
                      v-model="targetingSearch"
                      :items="statusFilteredConfigs"
                      :search-keys="configSearchKeys"
                      :sort-options="targetingSortOptions"
                      search-placeholder="Search path or variant…"
                      noun="configs"
                    >
                      <template #controls>
                        <div class="col-md-3 mb-2">
                          <b-form-select v-model="targetingFilter.status" size="sm" :options="targetingStatusOptions" />
                        </div>
                        <div class="col-md-3 mb-2">
                          <b-form-select
                            v-model="targetingFilter.surface"
                            size="sm"
                            :options="targetingSurfaceOptions"
                            value-field="value"
                            text-field="text"
                            data-testid="onboarding-targeting-surface-filter"
                          />
                        </div>
                      </template>
                      <template #default="{ rows }">
                        <div class="table-responsive">
                          <table class="table">
                            <thead>
                              <tr>
                                <th>Screen · target</th>
                                <th>v</th>
                                <th>Arms: variant · weight</th>
                                <th>Enabled</th>
                                <th class="text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="config in rows" :key="config.configKey">
                                <td>
                                  <span class="badge badge-info mr-1">{{ surfaceLabel(surfaceOf(config)) }}</span>
                                  <code>{{ config.configKey }}</code>
                                  <div v-if="config.matchType" class="text-muted" style="font-size:11px">
                                    {{ config.matchType }}: {{ config.matchValue }}
                                  </div>
                                </td>
                                <td>{{ config.version }}</td>
                                <td>
                                  <span
                                    v-for="(arm, index) in config.arms"
                                    :key="index"
                                    class="badge mr-1 mb-1"
                                    :class="arm.isControl ? 'badge-secondary' : 'badge-light border'"
                                  >
                                    {{ arm.variantKey }} {{ arm.weight }}%
                                    <span v-if="arm.isControl" class="ml-1">control</span>
                                  </span>
                                </td>
                                <td>
                                  <span v-if="config.enabled" class="badge badge-success">Enabled</span>
                                  <span v-else class="badge badge-secondary">Disabled</span>
                                  <span
                                    v-if="configWarning(config)"
                                    class="badge badge-warning d-block mt-1"
                                    :title="configWarning(config)"
                                  >
                                    <i class="fa fa-exclamation-triangle"></i> {{ configWarning(config) }}
                                  </span>
                                  <span
                                    v-for="warning in serverWarningsByConfig[config.configKey] || []"
                                    :key="warning.code"
                                    class="badge badge-warning d-block mt-1 text-wrap text-left"
                                    :title="warning.message"
                                  >
                                    <i class="fa fa-exclamation-triangle"></i> {{ warningLabel(warning) }}
                                  </span>
                                </td>
                                <td class="text-right text-nowrap">
                                  <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEditArms(config)">
                                    <i class="fa fa-pencil"></i> Edit arms
                                  </b-button>
                                  <b-button
                                    v-if="config.matchType === 'landing-path'"
                                    size="sm"
                                    variant="outline-primary"
                                    class="mr-1"
                                    @click="openRetarget(config)"
                                  >
                                    <i class="fa fa-random"></i> Retarget
                                  </b-button>
                                  <b-button size="sm" variant="outline-secondary" class="mr-1" @click="openHistory(config)">
                                    <i class="fa fa-history"></i> History
                                  </b-button>
                                  <b-button
                                    size="sm"
                                    :variant="config.enabled ? 'outline-warning' : 'outline-success'"
                                    :disabled="toggling[config.configKey]"
                                    @click="toggleEnabled(config)"
                                  >
                                    <i class="fa" :class="toggling[config.configKey] ? 'fa-spinner fa-spin' : (config.enabled ? 'fa-toggle-on' : 'fa-toggle-off')"></i>
                                    {{ config.enabled ? 'Disable' : 'Enable' }}
                                  </b-button>
                                </td>
                              </tr>
                              <tr v-if="!rows.length">
                                <td colspan="5" class="text-center text-muted">
                                  {{ flowConfigs.data.length ? 'No configs match the current filters.' : 'No flow configs found.' }}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </template>
                    </data-list>

                    <p class="text-muted" style="font-size:12px">
                      Everything created here is editable here: arms, split and enabled state edit in place (Edit arms appends
                      a new version). The one exception is the screen and target, which are immutable per config, so re-targeting
                      a landing-path config is offered as <strong>Retarget</strong>: it clones the arms into the New-journey
                      wizard onto a new path. An all-users config targets every user of its screen, so it has nothing to retarget.
                    </p>

                    <!-- ===================== LIVE TESTS (read-only) ===================== -->
                    <h6 class="text-uppercase text-muted mt-4" style="font-size:11px">Live tests</h6>
                    <div v-if="!runningExperiments.length" class="alert alert-secondary" style="font-size:12px">
                      No running experiments: an experiment is an enabled targeting config with more than one arm.
                      Enable a multi-arm config above to start one.
                    </div>
                    <div v-for="experiment in runningExperiments" :key="experiment.configKey" class="border rounded p-3 mb-2">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <span class="badge badge-info mr-1">{{ surfaceLabel(surfaceOf(experiment)) }}</span>
                          <code>{{ experiment.configKey }}</code>
                          <div v-if="experiment.matchType" class="text-muted" style="font-size:11px">
                            {{ experiment.matchType }}: {{ experiment.matchValue }}
                          </div>
                        </div>
                        <span class="badge badge-success">Running · v{{ experiment.version }}</span>
                      </div>
                      <div class="progress mb-2" style="height:10px">
                        <div
                          v-for="(arm, index) in experimentArms(experiment)"
                          :key="index"
                          class="progress-bar"
                          :style="{ width: arm.configuredPercent + '%', backgroundColor: armColour(index) }"
                        ></div>
                      </div>
                      <div style="font-size:12px">
                        <span v-for="(arm, index) in experimentArms(experiment)" :key="index" class="mr-3 d-inline-block">
                          <span
                            class="d-inline-block rounded-circle mr-1 align-middle"
                            :style="{ width: '10px', height: '10px', backgroundColor: armColour(index) }"
                          ></span>
                          <code>{{ arm.variantKey }}</code>
                          <span v-if="arm.isControl" class="badge badge-secondary ml-1">control</span>
                          · configured <strong>{{ arm.configuredPercent }}%</strong>
                        </span>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </b-tab>

            <!-- ===================== AREA 3: REFERENCE ===================== -->
            <b-tab title="Reference &amp; Glossary">
              <div class="card">
                <div class="card-body">
                  <b-tabs v-model="referencePill" pills>
                    <b-tab title="Glossary" active>
                      <h6 class="text-uppercase text-muted mt-3 mb-3" style="font-size:11px">Onboarding journey glossary</h6>
                      <data-list
                        :items="glossaryTerms"
                        :search-keys="glossarySearchKeys"
                        :initial-page-size="25"
                        :page-sizes="[25, 50]"
                        search-placeholder="Search terms…"
                        noun="terms"
                      >
                        <template #default="{ rows }">
                          <div class="table-responsive">
                            <table class="table table-sm">
                              <thead>
                                <tr>
                                  <th style="width:24%">Term</th>
                                  <th>Meaning</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="entry in rows" :key="entry.term">
                                  <td><strong>{{ entry.term }}</strong></td>
                                  <td>{{ entry.meaning }}</td>
                                </tr>
                                <tr v-if="!rows.length">
                                  <td colspan="2" class="text-center text-muted">No terms match the search.</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </template>
                      </data-list>
                    </b-tab>

                    <!-- Variants -->
                    <b-tab title="Variants">
                      <div class="text-center mt-3">
                        <i v-if="variants.loading" class="fa fa-spinner fa-spin"></i>
                      </div>
                      <div v-if="variants.error" class="alert alert-warning mt-3">{{ variants.error }}</div>

                      <template v-else-if="!variants.loading">
                        <h6 class="text-uppercase text-muted mt-3 mb-0" style="font-size:11px">Onboarding screen variants</h6>
                        <data-list
                          class="mt-3"
                          :items="variants.data"
                          :search-keys="variantSearchKeys"
                          :sort-options="variantSortOptions"
                          search-placeholder="Search variant key…"
                          noun="variants"
                        >
                          <template #default="{ rows }">
                            <div class="table-responsive">
                              <table class="table">
                                <thead>
                                  <tr>
                                    <th>Variant key</th>
                                    <th>Default</th>
                                    <th>Orphan arm</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="variant in rows" :key="variant">
                                    <td><code>{{ variant }}</code></td>
                                    <td>
                                      <span v-if="variant === variants.defaultVariant" class="badge badge-primary">Default</span>
                                    </td>
                                    <td>
                                      <span v-if="orphanVariantKeys.has('onboarding:' + variant)" class="badge badge-warning">Orphan</span>
                                    </td>
                                  </tr>
                                  <tr v-if="!rows.length">
                                    <td colspan="3" class="text-center text-muted">
                                      {{ variants.data.length ? 'No variants match the search.' : 'No variants in the manifest.' }}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </template>
                        </data-list>

                        <!-- Every other screen's variants, from the same build manifest (surfaces.<screen>.variants). -->
                        <div v-for="(keys, surface) in variants.surfaces" :key="surface" class="mt-4">
                          <h6 class="text-uppercase text-muted mb-2" style="font-size:11px">{{ surfaceLabel(surface) }} variants</h6>
                          <div class="table-responsive">
                            <table class="table">
                              <thead>
                                <tr>
                                  <th>Variant key</th>
                                  <th>Orphan arm</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="variant in keys" :key="surface + ':' + variant">
                                  <td><code>{{ variant }}</code></td>
                                  <td>
                                    <span v-if="orphanVariantKeys.has(surface + ':' + variant)" class="badge badge-warning">Orphan</span>
                                  </td>
                                </tr>
                                <tr v-if="!keys.length">
                                  <td colspan="2" class="text-center text-muted">No variants in the manifest for this screen.</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div v-if="orphanArmRows.length" class="table-responsive mt-3">
                          <h6 class="text-uppercase text-muted" style="font-size:11px">Orphan arms: arm points at a variant not in the current build</h6>
                          <table class="table table-sm">
                            <thead>
                              <tr>
                                <th>Config key</th>
                                <th>Variant key</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(row, index) in orphanArmRows" :key="index">
                                <td><code>{{ row.configKey }}</code></td>
                                <td><code>{{ row.variantKey }}</code></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </template>
                    </b-tab>

                  </b-tabs>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </div>

    <!-- ============================================================== -->
    <!-- EDIT ARMS modal (edit an existing config's arms → new version) -->
    <!-- ============================================================== -->
    <b-modal
      v-model="editArms.show"
      size="lg"
      title="Edit arms"
      :ok-disabled="!armsSummary(editArms.arms, editArms.surface).valid || editArms.saving"
      :ok-title="editArms.saving ? 'Saving…' : 'Save (new version)'"
      cancel-title="Cancel"
      @ok.prevent="saveArms"
    >
      <div class="mb-2">
        <code>{{ editArms.configKey }}</code>
        <span class="text-muted ml-2">v{{ editArms.baseVersion }} → draft v{{ editArms.baseVersion + 1 }}</span>
      </div>
      <div class="alert alert-info" style="font-size:12px">
        Editing arms creates version {{ editArms.baseVersion + 1 }} on save. Screen: {{ surfaceLabel(editArms.surface) }}.
        The screen and the match field ({{ editArms.matchType }}: <code>{{ editArms.matchValue }}</code>) are immutable;
        to change them, create a new config instead.
      </div>

      <arms-editor
        :arms="editArms.arms"
        :variants="variantsFor(editArms.surface)"
        @add="addArm(editArms.arms)"
        @remove="removeArm(editArms.arms, $event)"
        @control="setControl(editArms.arms, $event)"
      />

      <arms-validation :summary="armsSummary(editArms.arms, editArms.surface)" />

      <div v-if="editArms.error" class="alert alert-danger mt-2">{{ editArms.error }}</div>
    </b-modal>

    <!-- ============================================================== -->
    <!-- NEW-JOURNEY WIZARD modal (create end-to-end; retarget = clone) -->
    <!-- ============================================================== -->
    <b-modal
      v-model="wizard.show"
      size="lg"
      :title="wizardTitle"
      @hidden="resetWizard"
    >
      <div class="d-flex mb-3">
        <div
          v-for="(label, index) in wizardSteps"
          :key="index"
          class="flex-fill text-center pb-2 border-bottom"
          :class="wizard.step === index + 1 ? 'border-primary text-primary font-weight-bold' : 'text-muted'"
          style="font-size:12px"
        >
          <span
            class="badge mr-1"
            :class="wizard.step > index + 1 ? 'badge-success' : (wizard.step === index + 1 ? 'badge-primary' : 'badge-light border')"
          >{{ wizard.step > index + 1 ? '✓' : index + 1 }}</span>
          {{ label }}
        </div>
      </div>

      <template v-if="wizard.step === 1">
        <b-form-group
          label="Screen"
          description="Which screen this journey routes. Each screen has its own variants."
        >
          <b-form-radio-group
            v-model="wizard.surface"
            :options="SURFACES"
            value-field="value"
            text-field="text"
            :disabled="wizard.mode === 'retarget'"
            data-testid="onboarding-wizard-surface"
            @change="onWizardSurfaceChange"
          />
        </b-form-group>
        <template v-if="!wizardIsLandingPath">
          <div class="alert alert-info" style="font-size:12px">
            Match type: <code>all</code>. Every user of the {{ surfaceLabel(wizard.surface) }} enters this journey;
            the arms on the next step decide the random split. It is created <strong>disabled</strong>; enable it from
            Targeting once verified.
          </div>
          <div
            v-if="!variants.loading && !variantsFor(wizard.surface).length"
            class="alert alert-warning"
            style="font-size:12px"
          >
            The current build lists no {{ surfaceLabel(wizard.surface) }} variants, so there is nothing to pick yet.
            Deploy a build that publishes them, then try again.
          </div>
          <div v-if="wizardAllConfigExists" class="alert alert-warning" style="font-size:12px">
            The {{ surfaceLabel(wizard.surface) }} already has an all-users journey. Edit its arms in Targeting instead.
          </div>
        </template>
        <b-form-group
          v-if="wizardIsLandingPath"
          label="Landing paths (targets)"
          description="Pick one or more whitelisted paths. Each path gets its own journey with the same arms. Paths that already have a journey are not listed."
        >
          <multiselect
            v-if="landingPathsUsable"
            v-model="wizard.matchValues"
            :options="landingPathOptionValues"
            :multiple="true"
            :close-on-select="false"
            placeholder="Search landing paths"
            :show-labels="false"
            open-direction="bottom"
          />
          <b-form-textarea
            v-else
            v-model="wizard.manualPaths"
            rows="3"
            placeholder="/features/online-quiz-creator (one path per line)"
          />
        </b-form-group>
        <div v-if="wizardIsLandingPath && landingPaths.error" class="alert alert-warning" style="font-size:12px">
          Couldn't load the whitelisted landing-path list; enter paths manually, one per line. The server still
          validates each path against the whitelist at save.
        </div>
        <div v-if="wizardIsLandingPath" class="alert alert-info" style="font-size:12px">
          Match type: <code>landing-path</code>. New journeys are created <strong>disabled</strong>. Enable them from
          Targeting once verified. A path cannot be changed later; use Retarget to clone onto a new path.
        </div>
      </template>

      <template v-if="wizard.step === 2">
        <p v-if="wizard.surface === 'onboarding'" class="text-muted mb-2" style="font-size:12px">
          Each arm is a variant from the build plus a weight. Exactly one control;
          no duplicate variant; every variant must be in the build.
        </p>
        <p v-else class="text-muted mb-2" style="font-size:12px">
          Each arm is a {{ surfaceLabel(wizard.surface) }} variant from the build plus a weight. Exactly one control;
          no duplicate variant; every variant must be in the build.
        </p>
        <arms-editor
          :arms="wizard.arms"
          :variants="variantsFor(wizard.surface)"
          @add="addArm(wizard.arms)"
          @remove="removeArm(wizard.arms, $event)"
          @control="setControl(wizard.arms, $event)"
        />
        <arms-validation :summary="armsSummary(wizard.arms, wizard.surface)" />
      </template>

      <template v-if="wizard.step === 3">
        <dl class="row mb-0" style="font-size:13px">
          <dt class="col-sm-4 text-muted">Screen</dt>
          <dd class="col-sm-8">{{ surfaceLabel(wizard.surface) }}</dd>
          <dt class="col-sm-4 text-muted">
            Target{{ wizardPaths.length === 1 ? '' : 's' }} ({{ wizardPaths.length }})
          </dt>
          <dd class="col-sm-8">
            <div v-for="path in wizardPaths" :key="path" class="mb-1">
              <code>{{ path }}</code>
            </div>
            <span v-if="!wizardPaths.length" class="text-muted">not set</span>
            <div>
              <span class="badge badge-secondary">{{ wizardIsLandingPath ? 'landing-path' : 'all' }}</span>
              <span class="badge badge-light border ml-1">created disabled</span>
            </div>
          </dd>
          <dt class="col-sm-4 text-muted">Arms · configured split</dt>
          <dd class="col-sm-8">
            <span
              v-for="(arm, index) in reviewArms"
              :key="index"
              class="badge mr-1 mb-1"
              :class="arm.isControl ? 'badge-secondary' : 'badge-light border'"
            >
              {{ arm.variantKey }} {{ arm.configuredPercent }}%<span v-if="arm.isControl" class="ml-1">control</span>
            </span>
          </dd>
          <template v-if="wizard.createdPaths.length">
            <dt class="col-sm-4 text-muted">Already created</dt>
            <dd class="col-sm-8">
              <div v-for="path in wizard.createdPaths" :key="path" class="text-success mb-1">
                <i class="fa fa-check-circle"></i> <code>{{ path }}</code>
              </div>
            </dd>
          </template>
        </dl>
        <div class="alert alert-info mt-3" style="font-size:12px">
          On create, one config per path is saved <strong>disabled</strong> (v1) and appears in Targeting immediately,
          where you can Edit arms, Retarget, view History, or Enable it.
        </div>
      </template>

      <div v-if="wizard.failures.length" class="alert alert-danger mt-2" style="font-size:12px">
        <strong>{{ wizard.failures.length }} path{{ wizard.failures.length === 1 ? '' : 's' }} not created.</strong>
        Fix and press Create again; paths already created are not re-submitted.
        <ul class="mb-0 pl-3 mt-1">
          <li v-for="failure in wizard.failures" :key="failure.path">
            <code>{{ failure.path }}</code>: {{ failure.message }}
          </li>
        </ul>
      </div>

      <template #modal-footer>
        <div class="d-flex justify-content-between w-100">
          <b-button variant="outline-secondary" :disabled="wizard.step === 1 || wizard.saving" @click="wizardBack">
            Back
          </b-button>
          <div>
            <b-button variant="link" class="mr-2" :disabled="wizard.saving" @click="wizard.show = false">Cancel</b-button>
            <b-button
              v-if="wizard.step < wizardSteps.length"
              variant="primary"
              :disabled="!wizardStepValid"
              @click="wizardNext"
            >Next</b-button>
            <b-button
              v-else
              variant="primary"
              :disabled="!wizardCanCreate || wizard.saving"
              @click="createJourney"
            >{{ wizard.saving ? 'Creating…' : createButtonLabel }}</b-button>
          </div>
        </div>
      </template>
    </b-modal>

    <!-- ============================================================== -->
    <!-- VERSION HISTORY + ROLLBACK modal                               -->
    <!-- ============================================================== -->
    <b-modal v-model="history.show" size="lg" title="Version history" ok-only ok-title="Close">
      <div class="mb-2"><code>{{ history.configKey }}</code></div>
      <div class="text-center">
        <i v-if="history.loading" class="fa fa-spinner fa-spin"></i>
      </div>
      <div v-if="history.error" class="alert alert-warning">{{ history.error }}</div>
      <div v-else-if="!history.loading" class="table-responsive">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>v</th>
              <th>Arms: variant · weight</th>
              <th>Enabled</th>
              <th>Updated</th>
              <th class="text-right">Rollback</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ver in history.versions" :key="ver.version">
              <td>{{ ver.version }}</td>
              <td>
                <span
                  v-for="(arm, index) in ver.arms"
                  :key="index"
                  class="badge mr-1 mb-1"
                  :class="arm.isControl ? 'badge-secondary' : 'badge-light border'"
                >
                  {{ arm.variantKey }} {{ arm.weight }}%
                  <span v-if="arm.isControl" class="ml-1">control</span>
                </span>
              </td>
              <td>
                <span v-if="ver.enabled" class="badge badge-success">Enabled</span>
                <span v-else class="badge badge-secondary">Disabled</span>
              </td>
              <td class="text-muted" style="font-size:11px">
                {{ ver.updatedAt | dateTime }}
                <div v-if="ver.updatedBy">{{ ver.updatedBy }}</div>
                <div v-if="ver.notes" class="text-dark">{{ ver.notes }}</div>
              </td>
              <td class="text-right">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="history.rollingBack || ver.version === latestHistoryVersion"
                  @click="rollbackTo(ver)"
                >
                  <i class="fa fa-undo"></i> Roll back
                </b-button>
              </td>
            </tr>
            <tr v-if="!history.versions.length">
              <td colspan="5" class="text-center text-muted">No versions found.</td>
            </tr>
          </tbody>
        </table>
        <p class="text-muted" style="font-size:12px">Rolling back re-inserts an old version's arms as a new version on top; history is append-only and never rewritten.</p>
        <div v-if="history.rollbackError" class="alert alert-danger">{{ history.rollbackError }}</div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Config from "@/config"
import Multiselect from "vue-multiselect"
import ArmsEditor from "./components/ArmsEditor.vue"
import ArmsValidation from "./components/ArmsValidation.vue"
import DataList from "./components/DataList.vue"
import {
  ALL_MATCH_VALUE,
  SURFACES,
  addArm,
  armsSummary,
  editableArms,
  friendlyError,
  friendlyValidation,
  serialiseArms,
  setControl,
  surfaceLabel,
  surfaceMatchType,
  surfaceOf,
  unwrap,
} from "./journeyHelpers"

const TARGETING_TAB = 1
const REFERENCE_TAB = 2

const WARNING_LABELS = {
  FALLBACK_SHARE_HIGH: "Fallback share above 5%",
}

export default {
  name: "OnboardingJourney",
  components: { Multiselect, ArmsEditor, ArmsValidation, DataList },
  data() {
    return {
      activeArea: 0,
      referencePill: 0,
      wizardSteps: ["Target", "Variants & split", "Review"],
      targetingSearch: "",
      targetingFilter: { status: "all", surface: "all" },
      SURFACES,
      targetingSurfaceOptions: [{ value: "all", text: "All screens" }].concat(SURFACES),
      targetingStatusOptions: [
        { value: "all", text: "All statuses" },
        { value: "enabled", text: "Enabled only" },
        { value: "disabled", text: "Disabled only" },
      ],
      targetingSortOptions: [
        { value: "updated", text: "Recently updated", compare: (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0) },
        { value: "enabled", text: "Enabled first", compare: (a, b) => (Number(Boolean(b.enabled)) - Number(Boolean(a.enabled))) || String(a.configKey || "").localeCompare(String(b.configKey || "")) },
        { value: "path", text: "Landing path", compare: (a, b) => String(a.configKey || "").localeCompare(String(b.configKey || "")) },
        { value: "version", text: "Version (newest)", compare: (a, b) => (b.version || 0) - (a.version || 0) },
        { value: "arms", text: "Arm count", compare: (a, b) => ((b.arms || []).length - (a.arms || []).length) || String(a.configKey || "").localeCompare(String(b.configKey || "")) },
      ],
      variantSortOptions: [
        { value: "az", text: "A to Z", compare: (a, b) => String(a).localeCompare(String(b)) },
        { value: "za", text: "Z to A", compare: (a, b) => String(b).localeCompare(String(a)) },
      ],
      glossaryTerms: [
        { term: "Onboarding journey", meaning: "The onboarding experience a new user goes through after sign-up." },
        { term: "Acquisition journey", meaning: "An onboarding journey personalised by where the user came from (an acquisition landing page), not one shared flow for everyone." },
        { term: "Landing path (acquisitionPath)", meaning: "The landing-page path that brought the user in (e.g. /features/online-quiz-creator); the key used to pick a journey." },
        { term: "Variant (variant key)", meaning: "A specific onboarding UI flow (e.g. quiz-intent, ikea-checklist), taken from the app build manifest." },
        { term: "Arm", meaning: "A variant plus a weight inside a config; several arms in one config form an A/B split." },
        { term: "Flow config", meaning: "A targeting rule: for one screen, which users enter (the match type) and which arms split them. It has enabled or disabled state and versions." },
        { term: "Screen (surface)", meaning: "Which screen a flow config routes: the onboarding screen or the Home screen. Each screen has its own variants in the build manifest." },
        { term: "Match type", meaning: "How users enter a flow config, never which screen: landing-path (by the first landing page) or all (every user of the screen, split only by the arms)." },
        { term: "Targeting", meaning: "Deciding which users (by landing path) receive which journey, via flow configs." },
        { term: "Acquisition segment (intention)", meaning: "The intention or source the server derives for a user from where they came from." },
        { term: "Control (arm)", meaning: "The baseline arm in an experiment that the other arms are measured against." },
        { term: "Fallback", meaning: "The default journey used when no enabled config matches; a high fallback share means few users are personalised." },
        { term: "Fallback share", meaning: "The share of journeys that fell back; the dashboard warns when it is above the healthy threshold (currently 5%)." },
        { term: "Resolution source", meaning: "How a journey was chosen: flow-config, experiment, or fallback." },
        { term: "Orphan arm", meaning: "An arm pointing at a variant no longer present in the current build; a configuration fault to fix (healthy is 0)." },
        { term: "Experiment", meaning: "An enabled flow config with more than one arm, i.e. a live A/B test." },
        { term: "Version / rollback", meaning: "Every config edit creates a new version; a rollback re-applies an older version as a new one." },
      ],
      flowConfigs: { loading: false, error: null, data: [] },
      // data/defaultVariant are the onboarding screen's; surfaces holds every
      // other screen's variant list from the same build manifest.
      variants: { loading: false, error: null, data: [], defaultVariant: null, surfaces: {} },
      landingPaths: { loading: false, error: null, data: [] },
      health: { loading: false, error: null, data: { journeys: {}, resolutionSourceBreakdown: {}, fallbackShare: 0, orphanArms: [] } },
      toggling: {},
      editArms: { show: false, configKey: null, baseVersion: 0, surface: "onboarding", matchType: null, matchValue: null, arms: [], saving: false, error: null },
      wizard: this.emptyWizard(),
      history: { show: false, configKey: null, loading: false, error: null, versions: [], rollingBack: false, rollbackError: null },
    }
  },
  computed: {
    serverWarnings() {
      return Array.isArray(this.health.data.warnings) ? this.health.data.warnings : []
    },
    serverWarningsByConfig() {
      return this.groupWarnings("configKey")
    },
    fallbackShareRatio() {
      return this.health.data.fallbackShare || 0
    },
    fallbackSharePercent() {
      return (this.fallbackShareRatio * 100).toFixed(1) + '%'
    },
    resolutionSourceRows() {
      const breakdown = this.health.data.resolutionSourceBreakdown || {}
      const keys = Object.keys(breakdown)
      const total = keys.reduce((sum, key) => sum + (Number(breakdown[key]) || 0), 0)
      return keys.map((key) => {
        const value = breakdown[key]
        const percent = total > 0 ? Math.round(((Number(value) || 0) / total) * 1000) / 10 : 0
        return { key, value, percent }
      })
    },
    enabledConfigCount() {
      return (this.flowConfigs.data || []).filter((config) => config.enabled).length
    },
    statusFilteredConfigs() {
      const { status, surface } = this.targetingFilter
      return (this.flowConfigs.data || []).filter((config) => {
        if (status === "enabled" && !config.enabled) return false
        if (status === "disabled" && config.enabled) return false
        if (surface !== "all" && surfaceOf(config) !== surface) return false
        return true
      })
    },
    needsAttentionQueue() {
      const items = []
      ;(this.health.data.orphanArms || []).forEach((row) => {
        items.push({
          key: 'orphan:' + row.configKey + ':' + row.variantKey,
          severity: 'critical',
          description: 'Arm in ' + row.configKey + ' points at ' + row.variantKey + ', a variant not in the current build.',
          actionLabel: 'Fix arms',
          configKey: row.configKey,
        })
      })
      ;(this.flowConfigs.data || []).forEach((config) => {
        const warning = this.configWarning(config)
        if (warning === 'No control arm marked') {
          items.push({
            key: 'no-control:' + config.configKey,
            severity: 'warning',
            description: config.configKey + ' is enabled with more than one arm but no control arm marked.',
            actionLabel: 'Edit',
            configKey: config.configKey,
          })
        } else if (warning) {
          items.push({
            key: 'single-arm:' + config.configKey,
            severity: 'warning',
            description: config.configKey + ' is enabled with a single arm: a rollout, not an A/B test.',
            actionLabel: 'Review',
            configKey: config.configKey,
          })
        }
      })
      this.serverWarnings.forEach((warning, index) => {
        items.push({
          key: 'server:' + warning.code + ':' + (warning.configKey || '') + ':' + index,
          severity: 'warning',
          description: (warning.configKey ? warning.configKey + ': ' : '') + (warning.message || this.warningLabel(warning)),
          actionLabel: warning.configKey ? 'Fix arms' : 'See configs',
          configKey: warning.configKey || null,
        })
      })
      const serverReportsFallback = this.serverWarnings.some((warning) => warning.code === 'FALLBACK_SHARE_HIGH')
      if (!serverReportsFallback && this.fallbackShareRatio > 0.05 && this.enabledConfigCount > 0) {
        items.push({
          key: 'fallback-share',
          severity: 'warning',
          description: 'Fallback share is ' + this.fallbackSharePercent + ', above the 5% healthy threshold, while ' + this.enabledConfigCount + ' config(s) are enabled.',
          actionLabel: 'See configs',
          configKey: null,
        })
      }
      return items
    },
    configFaultCount() {
      return this.needsAttentionQueue.length
    },
    orphanArmRows() {
      return this.health.data.orphanArms || []
    },
    // "<screen>:<variant>": a key orphaned on one screen must not flag the
    // same key on another screen's list.
    orphanVariantKeys() {
      const surfaceByConfig = new Map((this.flowConfigs.data || []).map((config) => [config.configKey, surfaceOf(config)]))
      return new Set(this.orphanArmRows.map((row) => (surfaceByConfig.get(row.configKey) || "onboarding") + ":" + row.variantKey))
    },
    runningExperiments() {
      // A running experiment is an enabled targeting config that splits traffic,
      // i.e. it has more than one arm. A single-arm enabled config is a rollout, not a test.
      return (this.flowConfigs.data || []).filter((config) => config.enabled && (config.arms || []).length > 1)
    },
    runningExperimentCount() {
      return this.runningExperiments.length
    },
    latestHistoryVersion() {
      return this.history.versions.reduce((max, v) => Math.max(max, v.version || 0), 0)
    },
    wizardTitle() {
      return this.wizard.mode === "retarget"
        ? "Retarget → new journey (clone)"
        : "Create an onboarding journey"
    },
    wizardIsLandingPath() {
      return surfaceMatchType(this.wizard.surface) === "landing-path"
    },
    // A screen has at most one "all" config (its target is always "*"), so a
    // second create would 409: say so before the user fills in the arms.
    wizardAllConfigExists() {
      if (this.wizardIsLandingPath) return false
      return (this.flowConfigs.data || []).some(
        (config) => surfaceOf(config) === this.wizard.surface && config.matchType === "all"
      )
    },
    wizardPaths() {
      if (!this.wizardIsLandingPath) return [ALL_MATCH_VALUE]
      const paths = this.landingPathsUsable
        ? this.wizard.matchValues
        : this.wizard.manualPaths.split(/[\n,]/).map((path) => path.trim())
      return Array.from(new Set(paths.filter(Boolean)))
    },
    createButtonLabel() {
      const count = this.wizardPaths.length
      return count > 1 ? 'Create ' + count + ' journeys (disabled)' : 'Create (disabled by default)'
    },
    reviewArms() {
      return this.experimentArms({ arms: this.wizard.arms })
    },
    wizardStepValid() {
      if (this.wizard.step === 1) {
        return this.wizardPaths.length > 0 && !this.wizardAllConfigExists
          && this.variantsFor(this.wizard.surface).length > 0
      }
      if (this.wizard.step === 2) return this.armsSummary(this.wizard.arms, this.wizard.surface).valid
      return true
    },
    wizardCanCreate() {
      return this.wizardPaths.length > 0 && !this.wizardAllConfigExists
        && this.armsSummary(this.wizard.arms, this.wizard.surface).valid
    },
    landingPathsUsable() {
      return !this.landingPaths.error && this.landingPaths.data.length > 0
    },
    // Excludes paths already configured, so a fresh create doesn't set up a guaranteed
    // 409 on save; still always offers the field's own current values so they're never lost.
    landingPathOptionValues() {
      const configuredPaths = new Set(
        (this.flowConfigs.data || [])
          .filter((config) => config.matchType === 'landing-path')
          .map((config) => config.matchValue)
      )
      const selected = this.wizard.matchValues
      const values = (this.landingPaths.data || []).filter(
        (path) => !configuredPaths.has(path) || selected.indexOf(path) !== -1
      )
      selected.forEach((path) => {
        if (values.indexOf(path) === -1) values.push(path)
      })
      return values
    },
  },
  created() {
    this.fetchFlowConfigs()
    this.fetchVariants()
    this.fetchLandingPaths()
    this.fetchHealth()
  },
  methods: {
    emptyWizard() {
      return {
        show: false,
        mode: "create",
        step: 1,
        surface: "onboarding",
        matchValues: [],
        manualPaths: "",
        createdPaths: [],
        arms: [{ variantKey: "", weight: 100, isControl: true }],
        saving: false,
        failures: [],
      }
    },
    configSearchKeys(config) {
      return [config.configKey, config.matchValue]
        .concat((config.arms || []).map((arm) => arm.variantKey))
    },
    variantSearchKeys(variant) {
      return [variant]
    },
    glossarySearchKeys(entry) {
      return [entry.term, entry.meaning]
    },
    groupWarnings(field) {
      const grouped = {}
      this.serverWarnings.forEach((warning) => {
        const key = warning[field]
        if (!key) return
        if (!grouped[key]) grouped[key] = []
        grouped[key].push(warning)
      })
      return grouped
    },
    warningLabel(warning) {
      return WARNING_LABELS[warning.code] || warning.message || warning.code
    },
    resolveAttention(row) {
      this.goToTargeting(row.configKey)
    },
    goToTargeting(configKey) {
      this.targetingFilter.status = "all"
      this.targetingSearch = configKey || ""
      this.activeArea = TARGETING_TAB
      if (!configKey) return
      const config = (this.flowConfigs.data || []).find((c) => c.configKey === configKey)
      if (config) this.openEditArms(config)
    },
    fetchFlowConfigs() {
      this.flowConfigs.loading = true
      this.flowConfigs.error = null
      return this.$http
        .get(Config.api.onboardingFlowConfigs)
        .then((res) => {
          this.flowConfigs.data = unwrap(res, 'flowConfigs') || []
        })
        .catch((err) => {
          this.flowConfigs.error = friendlyError(err)
        })
        .finally(() => {
          this.flowConfigs.loading = false
        })
    },
    fetchVariants() {
      this.variants.loading = true
      this.variants.error = null
      this.$http
        .get(Config.api.onboardingVariants)
        .then((res) => {
          const data = unwrap(res, 'variants')
          // The build manifest is either a bare array of keys or { variants, defaultVariant }.
          if (Array.isArray(data)) {
            this.variants.data = data
            this.variants.defaultVariant = (res.body && res.body.defaultVariant) || null
          } else {
            this.variants.data = (data && data.variants) || []
            this.variants.defaultVariant = (data && data.defaultVariant) || null
            this.variants.surfaces = (data && data.surfaces) || {}
          }
        })
        .catch((err) => {
          this.variants.error = friendlyError(err)
        })
        .finally(() => {
          this.variants.loading = false
        })
    },
    fetchLandingPaths() {
      this.landingPaths.loading = true
      this.landingPaths.error = null
      this.$http
        .get(Config.api.onboardingLandingPaths)
        .then((res) => {
          const data = unwrap(res, 'landingPaths')
          this.landingPaths.data = Array.isArray(data) ? data : ((data && data.landingPaths) || [])
        })
        .catch((err) => {
          this.landingPaths.error = friendlyError(err)
        })
        .finally(() => {
          this.landingPaths.loading = false
        })
    },
    fetchHealth() {
      this.health.loading = true
      this.health.error = null
      this.$http
        .get(Config.api.onboardingHealth)
        .then((res) => {
          const data = unwrap(res, null) || {}
          this.health.data = { journeys: {}, resolutionSourceBreakdown: {}, fallbackShare: 0, orphanArms: [], ...data }
        })
        .catch((err) => {
          this.health.error = friendlyError(err)
        })
        .finally(() => {
          this.health.loading = false
        })
    },

    experimentArms(config) {
      const arms = config.arms || []
      const total = arms.reduce((sum, arm) => sum + (Number.isFinite(arm.weight) ? arm.weight : 0), 0)
      return arms.map((arm) => ({
        variantKey: arm.variantKey,
        isControl: Boolean(arm.isControl),
        weight: arm.weight,
        configuredPercent: total > 0 ? Math.round((arm.weight / total) * 1000) / 10 : 0,
      }))
    },
    armColour(index) {
      const palette = ['#4f9af2', '#7c5cff', '#28c840', '#febc2e', '#ff5f57', '#17a2b8']
      return palette[index % palette.length]
    },
    configWarning(config) {
      if (!config.enabled) return null
      const arms = config.arms || []
      if (arms.length <= 1) return 'Single arm: a rollout, not an A/B test'
      if (!arms.some((arm) => arm.isControl)) return 'No control arm marked'
      return null
    },

    addArm(arms) {
      addArm(arms)
    },
    removeArm(arms, index) {
      arms.splice(index, 1)
    },
    setControl(arms, index) {
      setControl(arms, index)
    },
    // Arms are checked against the variants of the config's own screen.
    armsSummary(arms, surface) {
      return armsSummary(arms, this.variantsFor(surface))
    },
    // Variants differ per screen, so arms picked for one screen are invalid for
    // another: start the arms over when the admin switches screen. A user
    // action only (not a watcher), so a retarget's cloned arms are never wiped.
    onWizardSurfaceChange() {
      this.wizard.arms = this.emptyWizard().arms
    },
    variantsFor(surface) {
      if (!surface || surface === "onboarding") return this.variants.data
      return this.variants.surfaces[surface] || []
    },
    surfaceOf(config) {
      return surfaceOf(config)
    },
    surfaceLabel(surface) {
      return surfaceLabel(surface)
    },

    openEditArms(config) {
      this.editArms = {
        show: true,
        configKey: config.configKey,
        baseVersion: config.version || 0,
        surface: surfaceOf(config),
        matchType: config.matchType,
        matchValue: config.matchValue,
        arms: editableArms(config.arms),
        saving: false,
        error: null,
      }
    },
    saveArms() {
      if (!this.armsSummary(this.editArms.arms, this.editArms.surface).valid) return
      this.editArms.saving = true
      this.editArms.error = null
      const payload = {
        configKey: this.editArms.configKey,
        arms: serialiseArms(this.editArms.arms),
        version: this.editArms.baseVersion,
      }
      this.$http
        .patch(Config.api.onboardingFlowConfigs, payload)
        .then(() => {
          this.$toasted.success('Arms saved as a new version.')
          this.editArms.show = false
          this.fetchHealth()
          return this.fetchFlowConfigs()
        })
        .catch((err) => {
          this.editArms.error = friendlyValidation(err)
          if (err && err.status === 409) this.fetchFlowConfigs()
        })
        .finally(() => {
          this.editArms.saving = false
        })
    },

    openGlossary() {
      this.activeArea = REFERENCE_TAB
      this.referencePill = 0
    },
    openCreateJourney() {
      const wizard = this.emptyWizard()
      wizard.mode = "create"
      wizard.show = true
      this.wizard = wizard
    },
    openRetarget(config) {
      // The landing path is immutable per config, so "edit the target" is a clone:
      // pre-fill the wizard with this config's arms onto a fresh, empty path.
      const wizard = this.emptyWizard()
      wizard.mode = "retarget"
      wizard.surface = surfaceOf(config)
      const clonedArms = editableArms(config.arms)
      if (clonedArms.length) wizard.arms = clonedArms
      wizard.show = true
      this.wizard = wizard
    },
    wizardBack() {
      if (this.wizard.step > 1) this.wizard.step -= 1
    },
    wizardNext() {
      if (this.wizardStepValid && this.wizard.step < this.wizardSteps.length) this.wizard.step += 1
    },
    resetWizard() {
      this.wizard = this.emptyWizard()
    },
    // The backend holds one config per landing path, so a multi-path create is N
    // independent POSTs. Created paths leave the selection, so a retry after a
    // partial failure only re-submits the ones that failed.
    createJourney() {
      if (!this.wizardCanCreate) return
      const wiz = this.wizard
      const arms = serialiseArms(wiz.arms)
      const surface = wiz.surface
      const matchType = surfaceMatchType(surface)
      wiz.saving = true
      wiz.failures = []
      const attempts = this.wizardPaths.map((matchValue) =>
        this.$http
          .post(Config.api.onboardingFlowConfigs, { surface, matchType, matchValue, arms, enabled: false })
          .then(() => ({ path: matchValue, created: true }))
          .catch((err) => ({ path: matchValue, created: false, message: friendlyValidation(err) }))
      )
      return Promise.all(attempts).then((results) => {
        const created = results.filter((result) => result.created).map((result) => result.path)
        const failures = results.filter((result) => !result.created)
        wiz.saving = false
        if (created.length) {
          wiz.createdPaths = wiz.createdPaths.concat(created)
          wiz.matchValues = wiz.matchValues.filter((path) => created.indexOf(path) === -1)
          wiz.manualPaths = failures.map((failure) => failure.path).join("\n")
          this.fetchFlowConfigs()
        }
        if (!failures.length) {
          wiz.show = false
          this.activeArea = TARGETING_TAB
          this.$toasted.success(
            created.length > 1
              ? created.length + " journeys created (disabled). Enable them from Targeting when ready."
              : "Journey created (disabled). Enable it from Targeting when ready."
          )
          return
        }
        wiz.failures = failures
        if (created.length) {
          this.$toasted.success(created.length + " of " + results.length + " journeys created (disabled).")
        }
      })
    },

    toggleEnabled(config) {
      this.$set(this.toggling, config.configKey, true)
      const payload = { configKey: config.configKey, enabled: !config.enabled, version: config.version }
      this.$http
        .patch(Config.api.onboardingFlowConfigs, payload)
        .then(() => {
          this.$toasted.success(config.enabled ? 'Config disabled.' : 'Config enabled.')
          this.fetchHealth()
          return this.fetchFlowConfigs()
        })
        .catch((err) => {
          this.$toasted.error(friendlyValidation(err))
          if (err && err.status === 409) this.fetchFlowConfigs()
        })
        .finally(() => {
          this.$set(this.toggling, config.configKey, false)
        })
    },

    openHistory(config) {
      this.history = { show: true, configKey: config.configKey, loading: true, error: null, versions: [], rollingBack: false, rollbackError: null }
      const url = Config.api.onboardingFlowConfigs + '/' + encodeURIComponent(config.configKey) + '/versions'
      this.$http
        .get(url)
        .then((res) => {
          const versions = unwrap(res, 'versions') || []
          this.history.versions = versions.slice().sort((a, b) => (b.version || 0) - (a.version || 0))
        })
        .catch((err) => {
          this.history.error = friendlyError(err)
        })
        .finally(() => {
          this.history.loading = false
        })
    },
    rollbackTo(ver) {
      this.history.rollingBack = true
      this.history.rollbackError = null
      // Rollback is an append: re-submit the old version's arms so the service
      // records them as a new version on top. History is never rewritten.
      const payload = {
        configKey: this.history.configKey,
        arms: serialiseArms(ver.arms || []),
        version: this.latestHistoryVersion,
      }
      this.$http
        .patch(Config.api.onboardingFlowConfigs, payload)
        .then(() => {
          this.$toasted.success('Rolled back to v' + ver.version + ' as a new version.')
          this.history.show = false
          this.fetchHealth()
          return this.fetchFlowConfigs()
        })
        .catch((err) => {
          this.history.rollbackError = friendlyValidation(err)
          if (err && err.status === 409) this.fetchFlowConfigs()
        })
        .finally(() => {
          this.history.rollingBack = false
        })
    },
  },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
